import { Component } from "@angular/core";
import { FeatureModalBaseComponent } from "@app-cmc/features/feature-modal-base.component";
import { CFSConnectionService, CompanyDataService } from "@app-cmc/services";
import { ToastService } from "@app-cmc/shared/components/app-toaster";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslocoService } from "@ngneat/transloco";
import { CouponService as CouponApiService, CouponCodeDisplayDto, CouponCodeItemStatus } from "clearline-api";
import { WidgetType } from "clearline-common";
import { of } from "rxjs";
import { catchError, map, take } from "rxjs/operators";
import { CouponCodeDisplay } from "./validate-coupon.model";

type ValidateCouponViewState = "search" | "details" | "loading";

@Component({
  selector: "app-validate-coupon-modal",
  templateUrl: "./validate-coupon-modal.component.html",
  styleUrls: ["./validate-coupon-modal.component.scss"]
})
export class ValidateCouponModalComponent extends FeatureModalBaseComponent {
  CouponCodeItemStatus = CouponCodeItemStatus;
  isRedeemed = false;
  loading = false;
  scannedCode = "";
  couponList: CouponCodeDisplay[] = [];
  selectedCoupon: CouponCodeDisplay = null;
  viewState: ValidateCouponViewState = "search";

  constructor(
    public activeModal: NgbActiveModal,
    private couponApiService: CouponApiService,
    private toastSvc: ToastService,
    private companyDataService: CompanyDataService,
    private cfsService: CFSConnectionService,
    private translocoService: TranslocoService
  ) {
    super(activeModal, WidgetType.ValidateCoupon);
  }

  search(code: string) {
    if (!code) {
      this.viewState = "search";
      this.isRedeemed = false;
      this.selectedCoupon = null;
      this.scannedCode = "";
      return;
    }

    this.viewState = "loading";
    this.setLoading(true);
    this.isRedeemed = false;

    this.couponApiService
      .getByCode(code)
      .pipe(
        take(1),
        map((couponList: CouponCodeDisplayDto[]) => couponList.map((coupon, i) => ({ ...coupon, id: i }))),
        catchError(() => of([]))
      )
      .subscribe((couponList: CouponCodeDisplay[]) => {
        this.couponList = couponList;
        this.viewState = this.couponList.length ? "details" : "search";

        if (!this.couponList.length) {
          this.toastSvc.danger(this.translocoService.translate("features.validateCoupon.invalidCoupon"));
        }

        this.setLoading(false);
      });
  }

  onCouponChange(coupon: CouponCodeDisplay) {
    this.selectedCoupon = coupon;
  }

  scan(code: string) {
    this.onScanModalClose();

    if (code && code.startsWith("http")) {
      code = code.split("/").pop();
    }

    this.scannedCode = code;
    this.search(this.scannedCode);
  }

  validate() {
    if (!!this.selectedCoupon) {
      this.viewState = "loading";
      this.setLoading(true);
      const { code, rewardProvider } = this.selectedCoupon;

      this.couponApiService
        .validateByCode({ code, rewardProvider })
        .pipe(take(1))
        .subscribe(
          () => {
            this.toastSvc.success(this.translocoService.translate("features.validateCoupon.couponValidated"));
            this.companyDataService.retrieveData();
            this.viewState = "details";
            this.setLoading(false);
            this.isRedeemed = true;
          },
          (e) => {
            const message = e?.error?.Message || this.translocoService.translate("features.validateCoupon.couponValidateError");
            this.toastSvc.danger(message);
            this.viewState = "details";
            this.setLoading(false);
          }
        );
    }
  }

  onScanModalOpened(): void {
    this.cfsService.toggleScanQrCodeModal(true).pipe(take(1)).subscribe();
  }

  private setLoading(loading: boolean): void {
    this.loading = loading;
  }

  private onScanModalClose(): void {
    this.cfsService
      .toggleScanQrCodeModal(false)
      .pipe(take(1))
      .subscribe(() => {});
  }
}
